import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaRobot } from 'react-icons/fa';
import { ConversationMessage } from '../hooks/useConversation';
import TypingIndicator from './TypingIndicator';

interface ConversationProps {
  messages: ConversationMessage[];
  isThinking: boolean;
  className?: string;
}

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Renders the assistant conversation with theme-aware bubbles, streaming text
 * and automatic scroll-to-bottom.
 */
const Conversation: React.FC<ConversationProps> = ({ messages, isThinking, className = '' }) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  return (
    <div className={`overflow-y-auto no-scrollbar flex flex-col gap-3 ${className}`}>
      {messages.length === 0 && !isThinking && (
        <div className="flex flex-col items-center justify-center text-center py-8">
          <FaRobot className="text-5xl text-primary/30 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Ask me anything about Sarfraz, or say "Projects dikhao" / "Dark mode on karo".
          </p>
        </div>
      )}

      {messages.map((message) => {
        const isUser = message.role === 'user';
        return (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[86%] p-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line break-words ${
                isUser
                  ? 'bg-gradient-to-r from-primary to-secondary text-white rounded-br-sm shadow-lg shadow-primary/20'
                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-sm shadow-md'
              }`}
            >
              {!isUser && (
                <div className="flex items-center gap-1 mb-1">
                  <FaRobot className="text-primary text-xs" />
                  <span className="text-[10px] font-semibold text-primary">GeniusMate</span>
                </div>
              )}
              <p>
                {message.content}
                {message.streaming && <span className="ml-0.5 inline-block w-1.5 h-4 bg-primary align-middle animate-pulse" />}
              </p>
              {message.content || !isUser ? (
                <p className={`text-[10px] mt-1 ${isUser ? 'text-white/70' : 'text-gray-400 dark:text-gray-500'}`}>
                  {formatTime(message.timestamp)}
                </p>
              ) : null}
            </div>
          </motion.div>
        );
      })}

      {isThinking && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-start"
        >
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 rounded-2xl rounded-bl-sm shadow-md">
            <div className="flex items-center gap-2">
              <FaRobot className="text-primary text-xs" />
              <TypingIndicator />
            </div>
          </div>
        </motion.div>
      )}

      <div ref={bottomRef} />
    </div>
  );
};

export default Conversation;
